import {RemoteConstantsModel} from "../models/remoteConstants.model";

// Given a unit and one attribute uid it has been assigned, return the list of
// attribute IDs (not uids) that this attribute requires but the unit is
// missing. Empty array means dependencies are satisfied.
//
// The dependency is declared on the metadata via `requires`. Single point of
// truth shared between the inline editor warning and the export warning pass.
export function getMissingUnitAttributeDependencies(
    unitAttributeUids: string[],
    attrUid: string,
    state: RemoteConstantsModel,
): string[] {
    const meta = state.unitAttributes?.find(m => m.uid === attrUid);
    if (!meta) return [];

    const required = meta.requires ?? [];
    if (required.length === 0) return [];

    const presentIds = new Set<string>(
        unitAttributeUids
            .map(uid => state.unitAttributes?.find(m => m.uid === uid)?.id)
            .filter((id): id is string => !!id)
    );

    return required.filter(id => !presentIds.has(id));
}

// Walk every unit and return one warning string per (unit, attribute,
// missingDependency) triple. Used by the export pre-flight.
export function getUnitAttributeDependencyWarnings(
    state: RemoteConstantsModel,
    name: (id: string) => string,
): string[] {
    const warnings: string[] = [];
    for (const unit of state.units ?? []) {
        for (const attrUid of unit.attributes ?? []) {
            const missing = getMissingUnitAttributeDependencies(unit.attributes, attrUid, state);
            if (missing.length === 0) continue;
            const attrMeta = state.unitAttributes?.find(m => m.uid === attrUid);
            const attrName = attrMeta ? name(attrMeta.uid) : attrUid;
            warnings.push(
                `Unit "${name(unit.id)}" has "${attrName}" but is missing required attribute${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}`
            );
        }
    }
    return warnings;
}

export function getMissingDependencyTooltip(missing: string[]): string {
    return `Requires: ${missing.join(', ')} (not present on this unit)`;
}

export interface DependencyLookup {
    (uid: string): string[]; // returns missing dep IDs for that attribute uid
}

// Builds the lookup once per render so each MetadataLabel doesn't re-scan
// state.unitAttributes for every uid in the list.
export function buildUnitDependencyLookup(
    unitAttributeUids: string[],
    state: RemoteConstantsModel,
): DependencyLookup {
    return (uid: string) => getMissingUnitAttributeDependencies(unitAttributeUids, uid, state);
}
